import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { fetchData } from '../../services/dataApi';
import { mergeGrammarSection } from '../../services/grammarLocalization';

const grammarPath = (langCode, file = 'index.json') => `grammar/${langCode}/${file}`;
const FALLBACK_PROBE = 'foundations/foundations_1.json';

async function fetchGrammarManifest(langCode) {
  try {
    return await fetchData(grammarPath(langCode));
  } catch (error) {
    if (langCode === 'en') throw error;

    // Localized lesson batches can arrive before their manifest. Only fall
    // back after proving that the requested locale actually has lesson data.
    await fetchData(grammarPath(langCode, FALLBACK_PROBE));
    const sourceManifest = await fetchData(grammarPath('en'));
    return {
      ...sourceManifest,
      locale: langCode,
      title: null,
      description: null,
      isSourceCatalogFallback: true,
    };
  }
}

function useGrammarCurriculum(langCode) {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!langCode) return;
    let active = true;
    setLoading(true);
    setError(false);
    setManifest(null);
    fetchGrammarManifest(langCode)
      .then((data) => active && setManifest(data))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [langCode]);

  return { manifest, loading, error };
}

function LessonCard({ lesson, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-violet-200 hover:bg-violet-50/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-black text-slate-900">{lesson.label || lesson.id.replaceAll('_', ' ')}</p>
          <p className="mt-1 text-xs capitalize text-slate-500">{lesson.category?.replaceAll('_', ' ')}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          {(lesson.levelRange || []).map((level) => (
            <span key={level} className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-black text-violet-700">{level}</span>
          ))}
        </div>
      </div>
    </button>
  );
}

function LessonView({ langCode, nativeLangCode, lesson, dir, nativeDir }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!lesson) return;
    let active = true;
    setLoading(true);
    setError(false);
    setData(null);
    Promise.all([
      fetchData(grammarPath(langCode, lesson.file)),
      nativeLangCode && nativeLangCode !== langCode
        ? fetchData(grammarPath(nativeLangCode, lesson.file)).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([target, native]) => active && setData({ target, native }))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [langCode, nativeLangCode, lesson]);

  if (!lesson) return null;
  if (loading) return <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />;
  if (error) return <div className="rounded-2xl border border-rose-100 bg-rose-50 p-5 text-sm font-bold text-rose-700">{t('grammar.lessonLoadError')}</div>;

  const allSections = data?.target?.sections || [];
  const matching = allSections.find((section) => section.id === lesson.id);
  if (!matching) {
    return <div className="rounded-2xl border border-rose-100 bg-rose-50 p-5 text-sm font-bold text-rose-700">{t('grammar.lessonSectionMissing')}</div>;
  }
  const nativeMatching = (data?.native?.sections || []).find(
    (section) => section.id === lesson.id,
  );
  const sections = [mergeGrammarSection(matching, nativeMatching)];

  return (
    <div dir={dir} className="space-y-5">
      {sections.map((section) => (
        <section key={section.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-r from-violet-50 to-indigo-50 px-5 py-5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-black text-slate-900">{section.label}</h2>
              {(lesson.levelRange || []).map((level) => <span key={level} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-violet-700 shadow-sm">{level}</span>)}
            </div>
            <p dir={nativeDir} className="mt-2 text-sm leading-relaxed text-slate-600">{section.concept}</p>
          </div>

          {section.learningObjectives?.length > 0 && (
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-violet-500">{t('grammar.learningObjectives')}</p>
              <ul dir={nativeDir} className="space-y-1.5 text-sm text-slate-600">
                {section.learningObjectives.map((objective) => <li key={objective}>• {objective}</li>)}
              </ul>
            </div>
          )}

          {section.patterns?.length > 0 && (
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-indigo-500">{t('grammar.patternsLabel')}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {section.patterns.map((pattern, index) => (
                  <div key={`${pattern.label}-${index}`} className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
                    <p dir={nativeDir} className="text-xs font-black text-indigo-700">{pattern.label}</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800">{pattern.form}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section.commonMistakes?.length > 0 && (
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-amber-600">{t('grammar.commonMistakes')}</p>
              <div className="space-y-3">
                {section.commonMistakes.map((mistake, index) => (
                  <div key={`${mistake.incorrect}-${index}`} className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <span className="font-semibold text-rose-700 line-through">{mistake.incorrect}</span>
                      <span className="font-black text-emerald-700">✓ {mistake.correct}</span>
                    </div>
                    <p dir={nativeDir} className="mt-1.5 text-xs leading-relaxed text-amber-900">{mistake.explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="divide-y divide-slate-100">
            {(section.examples || []).map((example) => (
              <article key={example.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-base font-black text-slate-900">{example.text}</p>
                  {example.level && <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-black text-emerald-700">{example.level}</span>}
                </div>
                {example.structure?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {example.structure.map((part, index) => (
                      <span key={`${part.role}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700">
                        <span className="font-black text-violet-600">{part.role}</span> · {part.text}
                      </span>
                    ))}
                  </div>
                )}
                {example.translationHint && <p dir={nativeDir} className="mt-3 text-xs italic leading-relaxed text-slate-500">{example.translationHint}</p>}
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function GrammarPage() {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const { langMap } = useLangMap();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const langCode = languagePrefs?.targetLanguage;
  const nativeLangCode = languagePrefs?.nativeLanguage;
  const lang = langCode ? langMap[langCode] : null;
  const nativeLang = nativeLangCode ? langMap[nativeLangCode] : null;
  const { manifest, loading, error } = useGrammarCurriculum(langCode);
  const lessonsById = useMemo(() => Object.fromEntries((manifest?.lessons || []).map((lesson) => [lesson.id, lesson])), [manifest]);
  const selected = selectedId ? lessonsById[selectedId] : null;

  useEffect(() => { setSelectedId(null); }, [langCode]);

  if (!langCode || !lang) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-4xl">🌍</p>
        <p className="text-lg font-black text-slate-800">{t('grammar.noLanguage')}</p>
        <p className="text-sm text-slate-500">{t('grammar.chooseFirst')}</p>
        <button onClick={() => navigate('/')} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white">{t('grammar.goHome')}</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 15% 0%, #ede9fe 0%, #f8fafc 50%, #ecfdf5 100%)' }}>
      <div className="mx-auto max-w-5xl space-y-7 px-4 py-10">
        <header className="flex items-center justify-between gap-4">
          <div>
            <p className="mb-1 text-xs font-black uppercase tracking-widest text-violet-600">{t('grammar.title')}</p>
            <h1 className="text-2xl font-black text-slate-900">{lang.label} {t('grammar.title')}</h1>
            <p className="mt-1 text-sm text-slate-500">{manifest?.description || t('grammar.subtitle')}</p>
          </div>
          <button onClick={() => selected ? setSelectedId(null) : navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm">
            {t('grammar.back')}
          </button>
        </header>

        {loading && <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>}
        {error && <div className="rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm"><p className="text-3xl">📭</p><p className="mt-3 font-bold text-slate-700">{t('grammar.notAvailable')}</p></div>}

        {!loading && !error && manifest && (selected ? (
          <LessonView
            langCode={langCode}
            nativeLangCode={nativeLangCode}
            lesson={selected}
            dir={lang.dir || 'ltr'}
            nativeDir={nativeLang?.dir || 'ltr'}
          />
        ) : (
          <div className="space-y-7">
            {(manifest.learningPath || []).map((path) => (
              <section key={path.id}>
                <h2 className="mb-3 text-lg font-black text-slate-900">{path.label}</h2>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {path.lessonIds.map((id) => lessonsById[id] && <LessonCard key={id} lesson={lessonsById[id]} onSelect={() => setSelectedId(id)} />)}
                </div>
              </section>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
